import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  FaUsers, FaUserCheck, FaUserPlus, FaUserClock,
  FaFilter, FaCalendarAlt, FaChevronDown, FaChevronUp,
  FaClock, FaHourglassHalf, FaSpinner, FaCheckCircle,
  FaChartPie, FaChartBar, FaChartLine,
  FaMoneyBillWave, FaUserFriends,
  FaArrowRight, FaExternalLinkAlt, FaTimes, FaHistory, FaExclamationTriangle
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Title, Tooltip, Legend, Filler
);

// ── Step titles ──
const STEP_TITLES = {
  1: "Initial Information Required",
  2: "Tax Return Preparation",
  3: "Draft Tax Return",
  4: "Return sent for filing"
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Helper to get initial state from localStorage
  const getInitialFilter = (key, defaultValue = "") => {
    const saved = localStorage.getItem("dashboardFiltersV2");
    if (!saved) return defaultValue;
    try {
      const parsed = JSON.parse(saved);
      return parsed[key] !== undefined ? parsed[key] : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };

  // Filter state
  const [taxYearStart, setTaxYearStart] = useState(getInitialFilter("taxYearStart", "2025-12-31"));
  const [taxYearEnd, setTaxYearEnd] = useState(getInitialFilter("taxYearEnd"));
  const [dueDateStart, setDueDateStart] = useState(getInitialFilter("dueDateStart", "2026-04-15"));
  const [dueDateEnd, setDueDateEnd] = useState(getInitialFilter("dueDateEnd"));
  const [serviceFilter, setServiceFilter] = useState(() => {
    const val = getInitialFilter("serviceFilter", ["tax"]);
    return Array.isArray(val) ? val : (val === "all" ? ["tax"] : [val]);
  });
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all"); // Always "all" now
  const [showFilters, setShowFilters] = useState(true);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const serviceDropdownRef = useRef(null);

  // Close service dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Persistence Effect
  useEffect(() => {
    const filters = { taxYearStart, taxYearEnd, dueDateStart, dueDateEnd, serviceFilter, paymentStatusFilter };
    localStorage.setItem("dashboardFiltersV3", JSON.stringify(filters));
  }, [taxYearStart, taxYearEnd, dueDateStart, dueDateEnd, serviceFilter, paymentStatusFilter]);

  // Escalations modal
  const [showEscalationsList, setShowEscalationsList] = useState(false);
  const [escalationsListData, setEscalationsListData] = useState([]);

  // Records list modal
  const [showRecordsList, setShowRecordsList] = useState(false);
  const [recordsListTitle, setRecordsListTitle] = useState("");
  const [recordsListData, setRecordsListData] = useState([]);

  // Debounce timer ref
  const debounceRef = useRef(null);

  // ── Fetch dashboard stats with filters ──
  const fetchDashboardStats = useCallback(async (filters = {}) => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/login"); return; }
    try {
      const params = new URLSearchParams();
      if (filters.taxYearStart) params.set("taxYearStart", filters.taxYearStart);
      if (filters.taxYearEnd) params.set("taxYearEnd", filters.taxYearEnd);
      if (filters.dueDateStart) params.set("dueDateStart", filters.dueDateStart);
      if (filters.dueDateEnd) params.set("dueDateEnd", filters.dueDateEnd);
      if (filters.serviceFilter && filters.serviceFilter.length > 0) {
        params.set("serviceFilter", filters.serviceFilter.join(","));
      }
      if (filters.paymentStatusFilter && filters.paymentStatusFilter !== "all") {
        params.set("paymentStatusFilter", filters.paymentStatusFilter);
      }

      const queryString = params.toString();
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/dashboard-stats${queryString ? `?${queryString}` : ""}`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to fetch dashboard stats");
      const data = await res.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Initial fetch
  useEffect(() => {
    fetchDashboardStats({
      taxYearStart, taxYearEnd, dueDateStart, dueDateEnd,
      serviceFilter, paymentStatusFilter
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch on filter change (debounced 400ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      fetchDashboardStats({
        taxYearStart, taxYearEnd, dueDateStart, dueDateEnd,
        serviceFilter, paymentStatusFilter
      });
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [taxYearStart, taxYearEnd, dueDateStart, dueDateEnd, serviceFilter, paymentStatusFilter, fetchDashboardStats]);

  // ── All records from API (already filtered server-side) ──
  const allRecords = useMemo(() => {
    if (!stats?.stepStatusRecords) return [];
    return [
      ...(stats.stepStatusRecords.unpaid || []),
      ...(stats.stepStatusRecords["wip-unpaid"] || []),
      ...(stats.stepStatusRecords["p-extension"] || []),
      ...(stats.stepStatusRecords["up-extension"] || []),
      ...(stats.stepStatusRecords["client-pending"] || []),
      ...(stats.stepStatusRecords["in-progress"] || []),
      ...(stats.stepStatusRecords.completed || [])
    ];
  }, [stats]);

  // ── Overall status counts (from server-filtered data) ──
  const overallStatusCounts = useMemo(() => {
    if (!stats?.stepStatusRecords) return { unpaid: 0, "wip-unpaid": 0, "p-extension": 0, "up-extension": 0, "client-pending": 0, "in-progress": 0, completed: 0 };
    return {
      unpaid: (stats.stepStatusRecords.unpaid || []).length,
      "wip-unpaid": (stats.stepStatusRecords["wip-unpaid"] || []).length,
      "p-extension": (stats.stepStatusRecords["p-extension"] || []).length,
      "up-extension": (stats.stepStatusRecords["up-extension"] || []).length,
      "client-pending": (stats.stepStatusRecords["client-pending"] || []).length,
      "in-progress": (stats.stepStatusRecords["in-progress"] || []).length,
      completed: (stats.stepStatusRecords.completed || []).length
    };
  }, [stats]);

  // ── Per-step status counts (from server-filtered stepBreakdown) ──
  const perStepCounts = useMemo(() => {
    if (!stats?.stepBreakdown) {
      const empty = { unpaid: 0, "wip-unpaid": 0, "in-progress": 0, "client-pending": 0, submitted: 0, total: 0 };
      return { 1: { ...empty }, 2: { ...empty }, 3: { ...empty }, 4: { ...empty } };
    }
    const sb = stats.stepBreakdown;
    const result = {};
    [1, 2, 3, 4].forEach(stepNum => {
      const key = `step${stepNum}`;
      const data = sb[key] || { unpaid: 0, "wip-unpaid": 0, "in-progress": 0, "client-pending": 0, submitted: 0 };
      result[stepNum] = {
        ...data,
        total: (data.unpaid || 0) + (data["wip-unpaid"] || 0) + (data["in-progress"] || 0) + (data["client-pending"] || 0) + (data.submitted || 0)
      };
    });
    return result;
  }, [stats]);

  // ── Open records modal filtered by a specific step + status ──
  const openStepStatusRecords = useCallback((stepNum, status, label) => {
    const data = allRecords.filter(r => {
      if (!r.steps) return false;
      const stepEntry = r.steps.find(s => s.step === stepNum);
      // Backend now sends 'unpaid' for 'pending' steps
      const matchStatus = status === 'unpaid' ? 'pending' : status;
      return stepEntry && stepEntry.status === matchStatus;
    });
    setRecordsListTitle(label);
    setRecordsListData(data);
    setShowRecordsList(true);
  }, [allRecords]);

  // ── Open records modal filtered by overall status ──
  const openOverallStatusRecords = useCallback((status) => {
    if (!stats?.stepStatusRecords) return;
    const labels = {
      unpaid: "Unpaid Records",
      "wip-unpaid": "WIP–Unpaid Records",
      "p-extension": "P Extension Records",
      "up-extension": "UP Extension Records",
      "client-pending": "Client Pending Records",
      "in-progress": "In Progress Records",
      completed: "Completed Records",
      "total-tax-filing": "Total Tax Filing Clients"
    };
    const data = status === "total-tax-filing" ? allRecords : (stats.stepStatusRecords[status] || []);
    setRecordsListTitle(labels[status]);
    setRecordsListData(data);
    setShowRecordsList(true);
  }, [stats, allRecords]);

  // ── Open records for metric cards ──
  const handleMetricCardClick = useCallback((type) => {
    switch (type) {
      case "totalClients":
        navigate("/client");
        break;
      case "activeServices": {
        const data = allRecords.filter(r => r.service);
        setRecordsListTitle("Active Service Clients");
        setRecordsListData(data);
        setShowRecordsList(true);
        break;
      }
      case "newThisWeek":
        navigate("/client");
        break;
      case "pendingActivation":
        navigate("/client");
        break;
      case "membersAdded": {
        const data = allRecords.filter(r => r.memberIndex !== null && r.memberIndex !== undefined);
        setRecordsListTitle("All Members");
        setRecordsListData(data);
        setShowRecordsList(true);
        break;
      }
      case "monthlyRevenue":
        navigate("/admin/payments");
        break;
      case "totalRevenue":
        navigate("/admin/payments");
        break;
      case "slaEscalations": {
        if (!stats) return;
        const allEscalations = [
          ...stats.services.taxFiling.escalations,
          ...stats.services.usaFormation.escalations
        ];
        setEscalationsListData(allEscalations);
        setShowEscalationsList(true);
        break;
      }
      default:
        navigate("/client");
    }
  }, [navigate, allRecords]);

  // ── Service card click ──
  const handleServiceCardClick = useCallback((serviceKey) => {
    if (!stats) return;
    const serviceNames = {
      taxFiling: "Tax Filing", usaFormation: "USA Company Formation",
      bookkeeping: "Bookkeeping", cfo: "CFO Advisory",
      registeredAgent: "Registered Agent", annualReport: "Annual Report"
    };
    const clients = stats.services[serviceKey]?.clients || [];
    setRecordsListTitle(`${serviceNames[serviceKey]} Clients`);
    setRecordsListData(clients.map(c => ({ ...c, service: serviceNames[serviceKey] })));
    setShowRecordsList(true);
  }, [stats]);

  // ── Tax Filing History modal ──
  const openTaxFilingHistory = useCallback(() => {
    if (!stats?.taxFilingHistory) return;
    setRecordsListTitle("Tax Filing History");
    setRecordsListData(stats.taxFilingHistory);
    setShowRecordsList(true);
  }, [stats]);

  const handleRecordClick = useCallback(() => { navigate("/client"); }, [navigate]);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const formatFullDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    return `${d.toLocaleDateString("en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}, ${d.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatPendingTime = (hours) => {
    if (hours < 24) return `${hours} Hours`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const getStatusBadge = (status) => {
    const map = {
      "unpaid": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      "pending": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      "wip-unpaid": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      "p-extension": "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      "up-extension": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
      "client-pending": "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      "in-progress": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      "submitted": "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      "completed": "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
    };
    return map[status] || "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  };

  const statusLabel = (status) => {
    const map = {
      "unpaid": "Unpaid",
      "pending": "Unpaid",
      "wip-unpaid": "WIP–Unpaid",
      "p-extension": "P Extension",
      "up-extension": "UP Extension",
      "client-pending": "Client Pending",
      "in-progress": "In Progress",
      "submitted": "Completed",
      "completed": "Completed"
    };
    return map[status] || status;
  };

  const hasActiveFilters = taxYearStart || taxYearEnd || dueDateStart || dueDateEnd || (serviceFilter?.length > 0 && !serviceFilter.includes("all")) || paymentStatusFilter !== "all";

  const clearAllFilters = () => {
    setTaxYearStart("2025-12-31"); setTaxYearEnd(""); setDueDateStart("2026-04-15"); setDueDateEnd("");
    setServiceFilter(["tax"]); setPaymentStatusFilter("all");
  };

  const toggleServiceInFilter = (service) => {
    if (service === "all") {
      setServiceFilter(["all"]);
      return;
    }
    let newFilter = [...serviceFilter].filter(s => s !== "all");
    if (newFilter.includes(service)) {
      newFilter = newFilter.filter(s => s !== service);
    } else {
      newFilter.push(service);
    }
    if (newFilter.length === 0) newFilter = ["tax"]; // Fallback to tax if nothing selected
    setServiceFilter(newFilter);
  };

  const serviceOptions = [
    { key: "all", label: "All Services" },
    { key: "tax", label: "Tax Filing" },
    { key: "usa", label: "USA Company" },
    { key: "bookkeeping", label: "Bookkeeping" },
    { key: "cfo", label: "CFO Advisory" },
    { key: "registeredAgent", label: "Registered Agent" },
    { key: "annualReport", label: "Annual Report" }
  ];

  // ── Chart Data (from server-filtered stats) ──
  const serviceDistributionData = useMemo(() => {
    if (!stats) return null;
    const s = stats.services;
    return {
      labels: ["Tax Filing", "USA Formation", "Bookkeeping", "CFO Advisory", "Registered Agent", "Annual Report"],
      datasets: [{ data: [s.taxFiling.total, s.usaFormation.total, s.bookkeeping.total, s.cfo.total, s.registeredAgent.total, s.annualReport.total], backgroundColor: ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4"], borderWidth: 0, hoverOffset: 8 }]
    };
  }, [stats]);

  const stepBreakdownData = useMemo(() => {
    if (!stats) return null;
    const sb = stats.stepBreakdown;
    return {
      labels: ["Step 1", "Step 2", "Step 3", "Step 4"],
      datasets: [
        { label: "Pending", data: [sb.step1.pending, sb.step2.pending, sb.step3.pending, sb.step4.pending], backgroundColor: "#f59e0b", borderRadius: 4 },
        { label: "In Progress", data: [sb.step1["in-progress"], sb.step2["in-progress"], sb.step3["in-progress"], sb.step4["in-progress"]], backgroundColor: "#3b82f6", borderRadius: 4 },
        { label: "Client Pending", data: [sb.step1["client-pending"], sb.step2["client-pending"], sb.step3["client-pending"], sb.step4["client-pending"]], backgroundColor: "#f97316", borderRadius: 4 },
        { label: "Completed", data: [sb.step1.submitted, sb.step2.submitted, sb.step3.submitted, sb.step4.submitted], backgroundColor: "#10b981", borderRadius: 4 }
      ]
    };
  }, [stats]);

  const monthlyTrendData = useMemo(() => {
    if (!stats) return null;
    const months = Object.keys(stats.monthlyRegistrations);
    const values = Object.values(stats.monthlyRegistrations);
    const labels = months.map(m => { const [y, mo] = m.split("-"); return new Date(y, mo - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" }); });
    return {
      labels,
      datasets: [{ label: "New Registrations", data: values, borderColor: "#3b82f6", backgroundColor: "rgba(59,130,246,0.1)", fill: true, tension: 0.4, pointBackgroundColor: "#3b82f6", pointBorderColor: "#fff", pointBorderWidth: 2, pointRadius: 4, pointHoverRadius: 6 }]
    };
  }, [stats]);

  // ── Loading & Error ──
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center bg-red-50 dark:bg-red-900/20 p-8 rounded-2xl">
          <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
          <button onClick={() => fetchDashboardStats({ taxYearStart, taxYearEnd, dueDateStart, dueDateEnd, serviceFilter, paymentStatusFilter })} className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">Retry</button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* ═══════════════ HEADER ═══════════════ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Overview of all clients, services, and progress
            {hasActiveFilters && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">Filtered</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <FaSpinner className="w-4 h-4 text-primary-500 animate-spin" />}
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all shadow-sm">
            <FaFilter className="w-3.5 h-3.5 text-primary-500" />
            {showFilters ? "Hide Filters" : "Show Filters"}
            {showFilters ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* ═══════════════ FILTERS ═══════════════ */}
      {showFilters && (
        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg shadow-sm p-5 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-dark-800">
            <FaFilter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-widest">Filters</h2>
            {hasActiveFilters && (
              <>
                <span className="text-[10px] font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded-full">Active</span>
                <button onClick={clearAllFilters}
                  className="ml-auto text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-wider transition-colors">
                  Clear All
                </button>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tax Year End (From)</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="date" value={taxYearStart} onChange={(e) => setTaxYearStart(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date (From)</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="date" value={dueDateStart} onChange={(e) => setDueDateStart(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all" />
              </div>
            </div>
            <div className="space-y-1.5" ref={serviceDropdownRef}>
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Services</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-700 transition-all focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <span className="truncate">
                    {serviceFilter.length === 0 ? "Select Services" :
                      serviceFilter.includes("all") ? "All Services" :
                        serviceFilter.length === 1 ? serviceOptions.find(o => o.key === serviceFilter[0])?.label :
                          `${serviceFilter.length} Services Selected`}
                  </span>
                  <FaChevronDown className={`w-3 h-3 transition-transform ${showServiceDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showServiceDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md shadow-lg max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                    <div className="p-1.5 space-y-0.5">
                      {serviceOptions.map((option) => {
                        const isSelected = serviceFilter.includes(option.key);
                        return (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => toggleServiceInFilter(option.key)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-all ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                          >
                            <div className={`w-4 h-4 border rounded flex items-center justify-center transition-all ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-dark-600'}`}>
                              {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>}
                            </div>
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment Status</label>
              <div className="relative">
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer appearance-none"
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="wip-unpaid">WIP–Unpaid</option>
                  <option value="p-extension">P Extension</option>
                  <option value="up-extension">UP Extension</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                  <FaChevronDown className="h-3 w-3" />
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ═══════════════ OVERALL STATUS CARDS ═══════════════ */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Workflow Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          {[
            { key: "total-tax-filing", label: "Total Tax Filing Clients", icon: FaUsers, accent: "border-t-indigo-500", iconColor: "text-indigo-500", bgAccent: "bg-indigo-50 dark:bg-indigo-500/10" },
            { key: "wip-unpaid", label: "WIP–Unpaid", icon: FaHourglassHalf, accent: "border-t-yellow-500", iconColor: "text-yellow-500", bgAccent: "bg-yellow-50 dark:bg-yellow-500/10" },
            { key: "p-extension", label: "P Extension", icon: FaHourglassHalf, accent: "border-t-purple-500", iconColor: "text-purple-500", bgAccent: "bg-purple-50 dark:bg-purple-500/10" },
            { key: "up-extension", label: "UP Extension", icon: FaHourglassHalf, accent: "border-t-indigo-500", iconColor: "text-indigo-500", bgAccent: "bg-indigo-50 dark:bg-indigo-500/10" },
            { key: "client-pending", label: "Client Pending", icon: FaHourglassHalf, accent: "border-t-orange-500", iconColor: "text-orange-500", bgAccent: "bg-orange-50 dark:bg-orange-500/10" },
            { key: "in-progress", label: "In Progress", icon: FaSpinner, accent: "border-t-blue-500", iconColor: "text-blue-500", bgAccent: "bg-blue-50 dark:bg-blue-500/10" },
            { key: "completed", label: "Completed", icon: FaCheckCircle, accent: "border-t-emerald-500", iconColor: "text-emerald-500", bgAccent: "bg-emerald-50 dark:bg-emerald-500/10" }
          ].map((card) => {
            const Icon = card.icon;
            const cardCount = card.key === 'total-tax-filing' ? (stats?.services?.taxFiling?.total || 0) : overallStatusCounts[card.key];
            return (
              <button key={card.key} onClick={() => openOverallStatusRecords(card.key)}
                className={`group flex flex-col p-4 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-left cursor-pointer border-t-[3px] ${card.accent}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-md ${card.bgAccent} ${card.iconColor}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{card.label}</span>
                  </div>
                  <FaArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{cardCount}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════ PER-STEP CARDS (Step 1 – Step 4) ═══════════════ */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Step Breakdown
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((stepNum) => {
            const counts = perStepCounts[stepNum];
            const stepTitle = STEP_TITLES[stepNum];
            const showClientPending = stepNum === 3 || stepNum === 4;

            const statItems = [
              { status: "in-progress", label: "In Progress", color: "text-blue-600 dark:text-blue-500", bg: "hover:bg-blue-50 dark:hover:bg-blue-500/10", count: counts["in-progress"] || 0 },
              ...(showClientPending ? [{ status: "client-pending", label: "Client Pending", color: "text-orange-600 dark:text-orange-500", bg: "hover:bg-orange-50 dark:hover:bg-orange-500/10", count: counts["client-pending"] || 0 }] : []),
              { status: "submitted", label: "Completed", color: "text-emerald-600 dark:text-emerald-500", bg: "hover:bg-emerald-50 dark:hover:bg-emerald-500/10", count: counts.submitted || 0 }
            ];

            return (
              <div key={stepNum} className="flex flex-col bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg shadow-sm">
                <div className="p-4 border-b border-gray-100 dark:border-dark-800">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2.5">
                      <div className="flex items-center justify-center w-6 h-6 rounded bg-gray-100 dark:bg-dark-800 text-xs font-bold text-gray-700 dark:text-gray-300">
                        {stepNum}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Step {stepNum}</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[120px] leading-tight">{stepTitle}</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-gray-900 dark:text-white">{counts["in-progress"] || 0}</span>
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  {statItems.map((item) => (
                    <button key={item.status} onClick={() => openStepStatusRecords(stepNum, item.status, `Step ${stepNum} — ${item.label}`)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md ${item.bg} transition-colors group cursor-pointer`}>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${item.color}`}>{item.count}</span>
                        <FaArrowRight className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══════════════ KEY METRICS ═══════════════ */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Key Metrics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
          {[
            { key: "totalClients", label: "Total Clients", value: stats.totalClients },
            { key: "filteredClients", label: "Filtered Clients", value: stats.filteredTotalClients || stats.totalClients },
            { key: "activeServices", label: "Active Services", value: stats.activeServices },
            { key: "newThisWeek", label: "New This Week", value: stats.newThisWeek },
            { key: "pendingActivation", label: "Pending Activation", value: stats.pendingActivation },
            { key: "membersAdded", label: "Total Members", value: stats.membersAdded },
            { key: "slaEscalations", label: "SLA Escalations", value: (stats.services.taxFiling.escalations.length + stats.services.usaFormation.escalations.length), color: "text-red-600" },

          ].map((metric) => (
            <button key={metric.key} onClick={() => handleMetricCardClick(metric.key)}
              className="group flex flex-col p-4 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg shadow-sm hover:border-primary-500/50 hover:shadow-md transition-all duration-200 text-left">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{metric.label}</span>
              <span className={`text-2xl font-bold ${metric.color || "text-gray-900 dark:text-white"} tracking-tight`}>{metric.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════ SERVICE CARDS ═══════════════ */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Services Portfolio
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: "taxFiling", label: "Tax Filing", count: stats.services.taxFiling.total, color: "bg-blue-500 text-blue-500", bgAccent: "bg-blue-50 dark:bg-blue-500/10" },
            { key: "usaFormation", label: "USA Formation", count: stats.services.usaFormation.total, color: "bg-purple-500 text-purple-500", bgAccent: "bg-purple-50 dark:bg-purple-500/10" },
            { key: "bookkeeping", label: "Bookkeeping", count: stats.services.bookkeeping.total, color: "bg-emerald-500 text-emerald-500", bgAccent: "bg-emerald-50 dark:bg-emerald-500/10" },
            { key: "cfo", label: "CFO Advisory", count: stats.services.cfo.total, color: "bg-amber-500 text-amber-500", bgAccent: "bg-amber-50 dark:bg-amber-500/10" },
            { key: "registeredAgent", label: "Registered Agent", count: stats.services.registeredAgent.total, color: "bg-red-500 text-red-500", bgAccent: "bg-red-50 dark:bg-red-500/10" },
            { key: "annualReport", label: "Annual Report", count: stats.services.annualReport.total, color: "bg-cyan-500 text-cyan-500", bgAccent: "bg-cyan-50 dark:bg-cyan-500/10" }
          ].map((svc) => (
            <button key={svc.key} onClick={() => handleServiceCardClick(svc.key)}
              className="group flex flex-col p-4 rounded-lg bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-200 text-left">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${svc.color.split(' ')[0]}`} />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">{svc.label}</span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{svc.count}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════ CHARTS ═══════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white dark:bg-dark-900 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700 p-5">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Service Distribution</h3>
          <div className="flex items-center justify-center" style={{ height: "260px" }}>
            {serviceDistributionData && <Doughnut data={{ ...serviceDistributionData, datasets: serviceDistributionData.datasets.map(d => ({ ...d, borderWidth: 0 })) }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { padding: 16, usePointStyle: true, pointStyle: "circle", font: { size: 11, family: "'Plus Jakarta Sans', sans-serif" } } } }, cutout: "75%" }} />}
          </div>
        </div>
        <div className="bg-white dark:bg-dark-900 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700 p-5">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Step-wise Breakdown</h3>
          <div style={{ height: "260px" }}>
            {stepBreakdownData && <Bar data={{ ...stepBreakdownData, datasets: stepBreakdownData.datasets.map(d => ({ ...d, borderRadius: 2, barPercentage: 0.6 })) }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { padding: 16, usePointStyle: true, pointStyle: "circle", font: { size: 11, family: "'Plus Jakarta Sans', sans-serif" } } } }, scales: { x: { stacked: true, grid: { display: false }, ticks: { font: { family: "'Plus Jakarta Sans', sans-serif" } } }, y: { stacked: true, beginAtZero: true, border: { display: false }, grid: { color: "rgba(156, 163, 175, 0.1)" }, ticks: { stepSize: 1, font: { family: "'Plus Jakarta Sans', sans-serif" } } } } }} />}
          </div>
        </div>
        <div className="bg-white dark:bg-dark-900 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700 p-5">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Registration Trend</h3>
          <div style={{ height: "260px" }}>
            {monthlyTrendData && <Line data={{ ...monthlyTrendData, datasets: monthlyTrendData.datasets.map(d => ({ ...d, tension: 0.4, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4 })) }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, ticks: { font: { family: "'Plus Jakarta Sans', sans-serif" } } }, y: { beginAtZero: true, border: { display: false }, grid: { color: "rgba(156, 163, 175, 0.1)" }, ticks: { stepSize: 1, font: { family: "'Plus Jakarta Sans', sans-serif" } } } }, interaction: { intersect: false, mode: 'index' } }} />}
          </div>
        </div>
      </div>

      {/* ═══════════════ TAX FILING HISTORY ═══════════════ */}
      {stats.taxFilingHistory && stats.taxFilingHistory.length > 0 && (
        <div className="bg-white dark:bg-dark-900 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FaHistory className="w-4 h-4 text-primary-500" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Tax Filing History
              </h3>
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                ({stats.taxFilingHistory.length} record{stats.taxFilingHistory.length !== 1 ? "s" : ""})
              </span>
            </div>
            <button onClick={openTaxFilingHistory}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-1 transition-colors">
              View All <FaExternalLinkAlt className="w-2.5 h-2.5" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-700">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">#</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Client</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Member</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Form</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tax Year End</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Due Date</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Completed</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Steps</th>
                </tr>
              </thead>
              <tbody>
                {stats.taxFilingHistory.slice(0, 10).map((hist, idx) => (
                  <tr key={idx} onClick={() => navigate("/client")} className="border-b border-gray-50 dark:border-dark-700/50 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 cursor-pointer transition-colors">
                    <td className="py-2.5 px-3 text-gray-400">{idx + 1}</td>
                    <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">{hist.name}</td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 text-xs">{hist.memberLabel}</td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300">{hist.assignedForm || "—"}</td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 text-xs">{formatDate(hist.taxYearEndDate)}</td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 text-xs">{formatDate(hist.dueDate)}</td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 text-xs">{formatDate(hist.completedAt)}</td>
                    <td className="py-2.5 px-3">
                      {hist.steps && hist.steps.length > 0 ? (
                        <div className="flex gap-1">
                          {hist.steps.map((s, si) => (
                            <span key={si} className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${s.status === "submitted" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                              s.status === "in-progress" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                                s.status === "client-pending" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" :
                                  "bg-gray-100 text-gray-500 dark:bg-dark-600 dark:text-gray-400"
                              }`}>{s.step}</span>
                          ))}
                        </div>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stats.taxFilingHistory.length > 10 && (
              <div className="mt-3 text-center">
                <button onClick={openTaxFilingHistory} className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
                  + {stats.taxFilingHistory.length - 10} more records
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ SLA ESCALATIONS SECTION ═══════════════ */}
      {(stats.services.taxFiling.escalations.length > 0 || stats.services.usaFormation.escalations.length > 0) && (
        <div className="bg-white dark:bg-dark-900 rounded-lg shadow-sm border border-orange-200 dark:border-orange-900/30 p-5 mt-5">
          <div className="flex items-center gap-2 mb-4">
            <FaClock className="w-4 h-4 text-orange-500" />
            <h3 className="text-xs font-bold text-orange-700 dark:text-orange-400 uppercase tracking-widest">
              SLA Escalations (Overdue Tasks)
            </h3>
            <span className="text-xs font-bold text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full">
              {stats.services.taxFiling.escalations.length + stats.services.usaFormation.escalations.length} Active
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-100 dark:border-dark-800">
                  <th className="pb-3 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Client / Member</th>
                  <th className="pb-3 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Type</th>
                  <th className="pb-3 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">Pending For</th>
                  <th className="pb-3 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right">Last Step Done At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-dark-800/50">
                {[...stats.services.taxFiling.escalations, ...stats.services.usaFormation.escalations].slice(0, 5).map((esc, i) => (
                  <tr key={i} className="group hover:bg-gray-50 dark:hover:bg-dark-800/50 transition-colors">
                    <td className="py-3 pr-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-tight">{esc.name}</span>
                        <span className="text-[10px] text-gray-500 font-medium">{esc.memberLabel}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                        {esc.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-xs font-bold text-red-500">{formatPendingTime(esc.hoursPending)}</span>
                    </td>
                    <td className="py-3 pl-4 text-right">
                      <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-tight">
                        {formatFullDate(esc.escalatedAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(stats.services.taxFiling.escalations.length + stats.services.usaFormation.escalations.length) > 5 && (
              <button onClick={() => handleMetricCardClick("slaEscalations")} className="w-full py-2 mt-2 text-[10px] font-bold text-primary-600 uppercase tracking-widest hover:bg-primary-50 transition-colors">
                View All Escalations
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ RECORDS LIST MODAL ═══════════════ */}
      {showRecordsList && createPortal(
        <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-4" onClick={() => setShowRecordsList(false)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  {recordsListTitle}
                  {recordsListTitle?.includes("History") && <FaHistory className="w-4 h-4 text-primary-500" />}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                  {recordsListData.length} record{recordsListData.length !== 1 ? "s" : ""} found
                </p>
              </div>
              <button onClick={() => setShowRecordsList(false)} className="p-2.5 hover:bg-gray-200 dark:hover:bg-dark-700 rounded-xl transition-all hover:rotate-90 duration-200">
                <FaTimes className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-auto custom-scrollbar">
              {recordsListData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400 dark:text-gray-500 gap-3 text-center">
                  <FaChartPie className="w-12 h-12 opacity-20" />
                  <p className="text-sm font-medium">No records found for this selection</p>
                </div>
              ) : (
                <div className="min-w-[1000px]">
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead className="sticky top-0 bg-white/95 dark:bg-dark-800/95 backdrop-blur-md z-10 shadow-sm">
                      <tr>
                        <th className="text-left py-4 px-6 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">#</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Client Info</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Service Profile</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Member Type</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Form</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Tax Year Details</th>
                        <th className="text-left py-4 px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700">Workflow Status</th>
                        <th className="text-left py-4 px-6 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-dark-700 ">Step Progress</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-dark-700/50">
                      {recordsListData.map((record, idx) => (
                        <tr key={idx} onClick={() => handleRecordClick(record)} className="group hover:bg-primary-50/40 dark:hover:bg-primary-900/10 cursor-pointer transition-all duration-150">
                          <td className="py-4 px-6 text-gray-400 dark:text-gray-600 font-mono text-xs">{String(idx + 1).padStart(2, '0')}</td>
                          <td className="py-4 px-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors uppercase tracking-tight">{record.name}</span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">{record.email}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 w-fit uppercase tracking-tight">
                                {record.service || "Tax Filing"}
                              </span>
                              {record.completedAt && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest">
                                  <FaCheckCircle className="w-2.5 h-2.5" /> History Cycle
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${record.memberLabel === 'Primary' ? 'bg-indigo-500' : 'bg-purple-500'}`}></div>
                              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-tighter">{record.memberLabel || "Primary"}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-flex items-center justify-center px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded text-[11px] font-bold text-gray-700 dark:text-gray-200 min-w-[50px]">
                              {record.assignedForm || "—"}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest ">Year End</span>
                              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest ">Due Date</span>
                              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{formatDate(record.taxYearEndDate)}</span>
                              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{formatDate(record.dueDate)}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm ${getStatusBadge(record.overallStatus)}`}>
                              {statusLabel(record.overallStatus)}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            {record.steps ? (
                              <div className="flex items-center justify-end gap-1.5">
                                {record.steps.map((s, si) => (
                                  <div key={si} className="relative group/step">
                                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-[11px] font-black transition-all duration-200 ${s.status === "submitted" ? "bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.3)]" :
                                      s.status === "in-progress" ? "bg-blue-500 text-white shadow-[0_0_10px_rgba(59,130,246,0.3)]" :
                                        s.status === "client-pending" ? "bg-orange-500 text-white shadow-[0_0_10px_rgba(249,115,22,0.3)] animate-pulse" :
                                          "bg-gray-100 text-gray-400 dark:bg-dark-700 dark:text-gray-600"
                                      }`}>
                                      {s.step}
                                    </span>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover/step:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                      {STEP_TITLES[s.step]}: {statusLabel(s.status)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex-none px-6 py-4 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/50 flex justify-end gap-3">
              <button onClick={() => setShowRecordsList(false)} className="px-5 py-2 text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                Close
              </button>
              <button onClick={handleRecordClick} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-primary-500/25 transition-all active:scale-95">
                Go to Clients List
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══════════════ ESCALATIONS LIST MODAL ═══════════════ */}
      {showEscalationsList && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={() => setShowEscalationsList(false)}></div>
          <div className="relative bg-white dark:bg-dark-900 w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col h-[85vh] overflow-hidden border border-gray-100 dark:border-dark-800">
            {/* Header */}
            <div className="flex-none px-6 py-5 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-white dark:bg-dark-900 sticky top-0 z-10">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
                  <FaExclamationTriangle className="text-red-500 w-5 h-5" />
                  SLA Escalations
                </h2>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">{escalationsListData.length} Overdue Records Found</span>
                </div>
              </div>
              <button onClick={() => setShowEscalationsList(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-full transition-colors text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6">
              {escalationsListData.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-dark-800 rounded-full flex items-center justify-center mb-4">
                    <FaCheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white uppercase">All clear</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">No tasks are currently outside their SLA window.</p>
                </div>
              ) : (
                <div className="border border-gray-100 dark:border-dark-800 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50/50 dark:bg-dark-800/50">
                      <tr>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest w-16 text-center">#</th>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Client Info</th>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Type</th>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Member / Index</th>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">Days Pending</th>
                        <th className="py-4 px-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right">Escalation Date & Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-800">
                      {escalationsListData.map((esc, i) => (
                        <tr key={i} className="group hover:bg-gray-50 dark:hover:bg-dark-800/50 transition-colors">
                          <td className="py-4 px-6 text-center text-xs font-bold text-gray-400 dark:text-gray-500 tabular-nums">{(i + 1).toString().padStart(2, '0')}</td>
                          <td className="py-4 px-6">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-900 dark:text-white uppercase tracking-tight">{esc.name}</span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">{esc.email}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                              {esc.type}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-tight">
                            {esc.memberLabel} {esc.memberIndex !== null ? `#${esc.memberIndex + 1}` : ''}
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span className="text-sm font-black text-red-600 tabular-nums">{formatPendingTime(esc.hoursPending)}</span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-tight">
                              {formatFullDate(esc.escalatedAt)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex-none px-6 py-4 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/50 flex justify-end gap-3">
              <button onClick={() => setShowEscalationsList(false)} className="px-5 py-2 text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                Close
              </button>
              <button onClick={() => { setShowEscalationsList(false); navigate("/client"); }} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-red-500/25 transition-all active:scale-95">
                Go to Active Clients
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dashboard;